import { createAction, RSAA } from 'redux-api-middleware' // RSAA = '@@redux-api-middleware/RSAA'
import Auth0LockPasswordless from 'auth0-lock'
import crypto from 'crypto-browserify'
import qs from 'query-string'
import moment from 'moment'

import i18n from '../services/i18n'

import config from '../config'
import { colors } from '../constants'
import { StringUtils } from '../utils'

const {
    app: { url: APP_URL },
    sso: { url: APIURL },
    auth0: { domain: AUTH0_DOMAIN, clientId: AUTH0_CLIENTID, logoutRedirectUrl: AUTH0_LOGOUT_REDIRECT },
} = config

const ActionTypes = {
    API_CALL_FAILURE: 'API_CALL##FAILURE',
    EXPIRED_SESSION: 'EXPIRED_SESSION',
    LOGIN_REQUEST: 'LOGIN##REQUEST',
    LOGIN_SUCCESS: 'LOGIN##SUCCESS',
    LOGIN_FAILURE: 'LOGIN##FAILURE',
    LOGOUT_REQUEST: 'LOGOUT##REQUEST',
    LOGOUT_SUCCESS: 'LOGOUT##SUCCESS',
    LOGOUT_FAILURE: 'LOGOUT##FAILURE',
    LOGIN_SESSION_REQUEST: 'LOGIN_SESSION##REQUEST',
    LOGIN_SESSION_SUCCESS: 'LOGIN_SESSION##SUCCESS',
    REFRESH_SESSION_ID: 'REFRESH_SESSION_ID',
    SAVE_SESSION: 'SAVE_SESSION',
    USER_DATA_REQUEST: 'USER_DATA##REQUEST',
    USER_DATA_SUCCESS: 'USER_DATA##SUCCESS',
    LOGIN_V2_STEP1: 'LOGIN_V2_STEP1',
    LOGIN_V2_STEP2_REQUEST: 'LOGIN_V2_STEP2##REQUEST',
    LOGIN_V2_STEP2_SUCCESS: 'LOGIN_V2_STEP2##SUCCESS',
    LOGIN_V2_STEP2_FAILURE: 'LOGIN_V2_STEP2##FAILURE',
    LOGIN_V2_FETCH_PROFILE_REQUEST: 'LOGIN_V2_FETCH_PROFILE##REQUEST',
    LOGIN_V2_FETCH_PROFILE_SUCCESS: 'LOGIN_V2_FETCH_PROFILE##SUCCESS',
    LOGIN_V2_FETCH_PROFILE_FAILURE: 'LOGIN_V2_FETCH_PROFILE##FAILURE',
    LOGIN_V2_LOGOUT_REQUEST: 'LOGIN_V2_LOGOUT##REQUEST',
    LOGIN_V2_LOGOUT_SUCCESS: 'LOGIN_V2_LOGOUT##SUCCESS',
    LOGIN_V2_LOGOUT_FAILURE: 'LOGIN_V2_LOGOUT##FAILURE',
    SAVE_REDIRECT_AFTER_LOGIN_PATH: 'SAVE_REDIRECT_AFTER_LOGIN_PATH',
}

/**
 * @deprecated Use ActionTypes
 */
export const Types = ActionTypes

function base64URLEncode(str) {
    return str.toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

function sha256(buffer) {
    return crypto.createHash('sha256').update(buffer).digest()
}

// Lock is sort of a singleton
let lockInstance

/**
 * Get auth0 lock
 */
function getLock() {
    if (!lockInstance) {
        lockInstance = new Auth0LockPasswordless(AUTH0_CLIENTID, AUTH0_DOMAIN, {
            auth: {
                redirect: false,
                redirectUrl: APP_URL,
                responseType: 'token id_token',
                params: {
                    scope: 'openid profile email',
                },
            },
            database: {
                allowSignUp: false,
            },
            theme: {
                logo: config.auth0.logo,
                primaryColor: colors.blue2,
            },
            languageDictionary: {
                forgotPasswordAction: i18n.t('ForgotPassword'),
                forgotPasswordInstructions: i18n.t('ForgotPasswordInstructions'),
                title: i18n.t('Login'),
                loginLabel: i18n.t('Login'),
                loginSubmitLabel: i18n.t('LogMeIn'),
                emailInputPlaceholder: i18n.t('EmailPlaceholder'),
                passwordInputPlaceholder: i18n.t('Password'),
                signUpTerms: i18n.t('SignUpTerms'),
            },
        })
    }

    return lockInstance
}

/**
 * Logout function depending if auth0 is enabled
 */
function logout() {
    return dispatch => {
        const out = dispatch({
            type: ActionTypes.LOGOUT_SUCCESS,
        })

        getLock().logout({ returnTo: AUTH0_LOGOUT_REDIRECT })

        return out
    }
}

/**
 * Logout user dued expired session
 */
function expiredSession() {
    return (dispatch) => {
        const out = dispatch({
            type: ActionTypes.EXPIRED_SESSION,
        })

        dispatch(logout())

        return out
    }
}

/**
 * Logs into the app with an already authenticated session token
 * @param {String} sessionId The session token
 */
function loginWithSession(sessionId) {
    return (dispatch) => {
        dispatch({
            type: ActionTypes.SAVE_SESSION,
            payload: { sessionId },
        })

        return dispatch({
            [RSAA]: {
                endpoint: `${APIURL}/login/`,
                method: 'GET',
                types: [ActionTypes.LOGIN_SESSION_REQUEST, ActionTypes.LOGIN_SESSION_SUCCESS, ActionTypes.API_CALL_FAILURE],
            },
        })
    }
}

/**
 *
 * @returns
 */
function loginV2() {
    return (dispatch) => {
        const authState = base64URLEncode(crypto.randomBytes(32))
        const verifier = base64URLEncode(crypto.randomBytes(32))
        const challenge = base64URLEncode(sha256(verifier))

        dispatch({
            type: ActionTypes.LOGIN_V2_STEP1,
            payload: { authState, verifier, challenge },
        })

        const redirectUrl = qs.stringifyUrl({
            url: `https://${config.auth0.domain}/authorize`,
            query: {
                response_type: 'code',
                code_challenge: challenge,
                code_challenge_method: 'S256',
                client_id: config.auth0.clientId,
                redirect_uri: window.location.origin,
                state: authState,
                scope: 'openid profile email',
            },
        })

        window.location.replace(redirectUrl)
    }
}

/**
 *
 * @param {string} accessToken
 * @param {string} idToken
 * @param {number} expiresIn
 * @returns
 */
function loginV2FetchProfile(accessToken, idToken, expiresIn) {
    return async (dispatch) => {
        const response = await dispatch(
            createAction({
                endpoint: `https://${config.auth0.domain}/userinfo`,
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                types: [ActionTypes.LOGIN_V2_FETCH_PROFILE_REQUEST, ActionTypes.LOGIN_V2_FETCH_PROFILE_SUCCESS, ActionTypes.LOGIN_V2_FETCH_PROFILE_FAILURE],
            })
        )

        if (response.type !== ActionTypes.LOGIN_V2_FETCH_PROFILE_SUCCESS) {
            return response
        }

        const profile = response.payload
        
        const estateSpace = `https://${config.auth0.domain}/estates`

        const legacyNamespace = config.auth0.tenantDomain.replace(/\./g, ':')
        const legacyEstateSpace = `https://${legacyNamespace}/estates`

        // Look for the estates information. If not in the main namespace,
        // get the legacy namespace
        const estates = profile[estateSpace] ?? profile[legacyEstateSpace]
        
        const expiresAt = new Date(new Date().getTime() + expiresIn * 1000)

        return dispatch({
            type: ActionTypes.LOGIN_SUCCESS,
            payload: {
                estates,
                email: profile.email,
                sessionId: StringUtils.md5(accessToken),
                idToken,
                accessToken,
                expiresAt: expiresAt.toJSON(),
            },
        })
    }
}

/**
 *
 * @returns
 */
function fetchOwnProfile() {
    return async (dispatch, getState) => {
        dispatch({ type: ActionTypes.USER_DATA_REQUEST })

        const state = getState()

        const accessToken = state?.user?.session?.accessToken
        const idToken = state?.user?.session?.idToken
        const expiresAt = state?.user?.session?.expiresAt
        const expiresIn = Math.floor(moment(expiresAt).diff(moment()) / 1000)

        if (!accessToken || !idToken || !expiresAt) {
            return dispatch({
                type: ActionTypes.API_CALL_FAILURE,
                payload: { message: 'Cannot get tokens' },
            })
        }

        await dispatch(loginV2FetchProfile(accessToken, idToken, expiresIn))
        return dispatch({ type: ActionTypes.USER_DATA_SUCCESS, payload: {} })
    }
}

/**
 *
 * @param {string} authCode
 * @param {string} authState
 * @returns
 */
function loginV2Resume(authCode, authState) {
    return async (dispatch, getState) => {
        const state = getState()
        const prevAuthState = state?.user?.authV2?.authState
        const verifier = state?.user?.authV2?.verifier

        if (prevAuthState !== authState) {
            return dispatch({
                type: ActionTypes.LOGIN_V2_STEP2_FAILURE,
                payload: { name: 'wrong_state', message: 'state does not match' },
            })
        }

        const data = {
            grant_type: 'authorization_code',
            client_id: config.auth0.clientId,
            code_verifier: verifier,
            code: authCode,
            redirect_uri: window.location.origin,
        }

        const response = await dispatch(
            createAction({
                endpoint: `https://${config.auth0.domain}/oauth/token`,
                method: 'POST',
                body: JSON.stringify(data),
                types: [ActionTypes.LOGIN_V2_STEP2_REQUEST, ActionTypes.LOGIN_V2_STEP2_SUCCESS, ActionTypes.LOGIN_V2_STEP2_FAILURE],
            })
        )

        if (response.type !== ActionTypes.LOGIN_V2_STEP2_SUCCESS) {
            return response
        }

        const { access_token: accessToken, id_token: idToken, expires_in: expiresIn } = response.payload

        return dispatch(loginV2FetchProfile(accessToken, idToken, expiresIn))
    }
}

function redirectAfterLogin(path) {
    return {
        type: ActionTypes.SAVE_REDIRECT_AFTER_LOGIN_PATH,
        payload: {
            path,
        },
    }
}

export { ActionTypes, logout, expiredSession, fetchOwnProfile, loginWithSession, loginV2, loginV2Resume, redirectAfterLogin }
